import { sanitiseSubdomain } from '@squaredup/tenants';
import Config from 'config';
import { isPerBranchEnvironment } from 'lib/environment';
import { findTenantSelectionRedirectUrl } from './findTenantSelectionRedirectUrl';
import { isReservedSubdomain } from './getApplicationSubdomain';

interface RedirectOffForeignSubdomainParams {
    /** The URL the app is currently loaded on. */
    currentUrl: URL;

    /** The id of the tenant being resolved, whether just picked or restored on load. */
    tenantId: string;

    /** The tenant's own configured subdomain, if any. */
    tenantSubdomain: string | undefined;
}

/**
 * Navigate off the current tenant subdomain when the given tenant belongs elsewhere, carrying the
 * selection across the origin boundary. Used both when a tenant is picked and when one is restored on
 * load, so a user never views a tenant on a subdomain that fronts a different set of tenants.
 *
 * Returns true when a redirect was initiated, in which case the page will unload and the caller should
 * stop.
 *
 * In DEV, EU DEV and Local the first hostname label is the deployment stage rather than a tenant subdomain, so the
 * shared host computed by stripping it does not exist. A redirect to a tenant's own subdomain still
 * works (the enforced-subdomain gate relies on the same), but a redirect to the shared host cannot, so
 * that case alone is suppressed and logged, and this returns false.
 */
export const redirectOffForeignSubdomain = ({
    currentUrl,
    tenantId,
    tenantSubdomain
}: RedirectOffForeignSubdomainParams): boolean => {
    const redirectUrl = findTenantSelectionRedirectUrl({ currentUrl, tenantId, tenantSubdomain });
    if (!redirectUrl) {
        return false;
    }

    const targetSubdomain = sanitiseSubdomain(tenantSubdomain);
    const isRedirectToSharedHost = !targetSubdomain || isReservedSubdomain(targetSubdomain);

    if (isRedirectToSharedHost && isPerBranchEnvironment(Config.Environment)) {
        // eslint-disable-next-line no-console
        console.log(`Would redirect off the subdomain to ${redirectUrl}`);
        return false;
    }

    window.location.assign(redirectUrl);

    return true;
};
